import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link2, Lock, Users, X } from 'lucide-react';
import driveApi, { errorMessage, ROLE_LABELS, ROLES } from '../../api/drive';
import { useAuth } from '../../contexts/AuthProvider';
import { useDrive } from '../../contexts/DriveProvider';
import { initials } from '../../lib/files';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileTag } from './ItemsView';
import GuestLinks from './GuestLinks';

const GRANTABLE = [ROLES.VIEWER, ROLES.EDITOR, ROLES.MANAGER];

const RoleSelect = ({ value, max, onChange, label, disabled }) => (
  <select
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(Number(e.target.value))}
    className="h-9 rounded-lg border border-border bg-card px-2 text-sm disabled:opacity-60"
  >
    {GRANTABLE.filter((r) => r <= max || r === value).map((r) => (
      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
    ))}
  </select>
);

const Principal = ({ entry }) => {
  if (entry.team) {
    return (
      <>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-white" style={{ background: entry.team.color }}>
          <Users className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <b className="truncate">{entry.team.name}</b>
          <span className="truncate text-xs text-muted-foreground">
            {entry.team.member_count} member{entry.team.member_count === 1 ? '' : 's'}
            {entry.inherited && `, via ${entry.source}`}
          </span>
        </div>
      </>
    );
  }
  return (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {initials(entry.user.name)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="truncate">{entry.user.name}</b>
        <span className="truncate text-xs text-muted-foreground">
          {entry.user.email}
          {entry.inherited && `, via ${entry.source}`}
        </span>
      </div>
    </>
  );
};

/**
 * Share a node (folder/file) or a whole shared drive with LampTwist people and teams.
 * target: { type: 'node' | 'drive', id, name, item? }
 */
const isEmail = (q) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim());

const ShareDialog = ({ item, drive, onOpenChange, focusGuests = false }) => {
  const target = item ? { type: 'node', id: item.id, name: item.name } : drive ? { type: 'drive', id: drive.id, name: drive.name } : null;
  const open = !!target;
  const { user } = useAuth();
  const { toast, bump } = useDrive();
  const [state, setState] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ people: [], teams: [] });
  const [newRole, setNewRole] = useState(ROLES.VIEWER);
  const [guestEmails, setGuestEmails] = useState('');
  const guestsRef = useRef(null);
  const scrollToGuests = () => setTimeout(() => guestsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);

  const load = useCallback(async () => {
    if (!target) return;
    try {
      setState(target.type === 'node' ? await driveApi.access(target.id) : await driveApi.driveAccess(target.id));
    } catch (err) {
      toast(errorMessage(err, 'Could not load access.'), 'error');
    }
  }, [target?.type, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setState(null);
      setQuery('');
      setNewRole(ROLES.VIEWER);
      setGuestEmails('');
      load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults({ people: [], teams: [] });
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        setResults(await driveApi.directory(query.trim()));
      } catch {
        setResults({ people: [], teams: [] });
      }
    }, 220);
    return () => clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (open && focusGuests && state) scrollToGuests();
  }, [open, focusGuests, !!state]); // eslint-disable-line react-hooks/exhaustive-deps

  const myRole = state?.role || 0;
  const canShare = myRole >= ROLES.EDITOR;
  const canManage = myRole >= ROLES.MANAGER;

  const add = async (body) => {
    try {
      const fn = target.type === 'node' ? driveApi.grant : driveApi.grantDrive;
      await fn(target.id, { ...body, role: newRole });
      setQuery('');
      await load();
      bump();
    } catch (err) {
      toast(errorMessage(err, 'Could not share.'), 'error');
    }
  };

  const change = async (entry, role) => {
    try {
      await driveApi.changeAccess(entry.id, role);
      await load();
    } catch (err) {
      toast(errorMessage(err, 'Could not change access.'), 'error');
    }
  };

  const revoke = async (entry) => {
    try {
      await driveApi.revokeAccess(entry.id);
      await load();
      bump();
    } catch (err) {
      toast(errorMessage(err, 'Could not remove access.'), 'error');
    }
  };

  const hasResults = results.people.length + results.teams.length > 0;
  // Someone typed an outside email: they can't be added as a person, offer a guest link instead.
  const colleagueDomain = /@lamptwist\.be$/i.test(query.trim());
  const outsideEmail = target?.type === 'node' && isEmail(query) && !hasResults && !colleagueDomain ? query.trim() : '';
  // A colleague who never signed in: add them by email, the access waits for their first sign-in
  const newColleague = isEmail(query) && !hasResults && colleagueDomain ? query.trim().toLowerCase() : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[616px] overflow-y-auto p-0">
        <div className="flex items-start gap-3 px-6 pb-4 pt-5">
          {item ? <FileTag item={item} /> : <span className="h-8 w-8 rounded-lg" style={{ background: drive?.color }} aria-hidden="true" />}
          <DialogHeader className="min-w-0 flex-1">
            <DialogTitle className="truncate">Share “{target?.name}”</DialogTitle>
            <DialogDescription>
              {target?.type === 'drive' ? 'Everyone added here gets access to the whole drive.' : 'People and teams added here get access to this item and everything inside it.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        {canShare && (
          <div className="relative px-6">
            <div className="flex gap-2">
              <label className="flex h-10 flex-1 items-center rounded-lg border border-input focus-within:border-ring px-3">
                <span className="sr-only">Add people or teams</span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={target?.type === 'node' ? 'Add people, teams or an outside email' : 'Add people or teams'}
                  className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
                />
              </label>
              <RoleSelect value={newRole} max={myRole} onChange={setNewRole} label="Role for new people" />
            </div>
            {hasResults && (
              <ul className="absolute left-6 right-6 top-[52px] z-10 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover py-1 shadow-xl">
                {results.teams.map((t) => (
                  <li key={`t${t.id}`}>
                    <button type="button" onClick={() => add({ team_id: t.id })} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white" style={{ background: t.color }}><Users className="h-4 w-4" aria-hidden="true" /></span>
                      <span className="flex-1"><b>{t.name}</b> <span className="text-xs text-muted-foreground">{t.member_count} members</span></span>
                    </button>
                  </li>
                ))}
                {results.people.filter((p) => p.id !== user?.user_id).map((p) => (
                  <li key={`p${p.id}`}>
                    <button type="button" onClick={() => add({ user_id: p.id })} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{initials(p.name)}</span>
                      <span className="flex min-w-0 flex-1 flex-col"><b className="truncate">{p.name}</b><span className="truncate text-xs text-muted-foreground">{p.email}</span></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {newColleague && (
              <div className="absolute left-6 right-6 top-[52px] z-10 rounded-xl border border-border bg-popover py-1 shadow-xl">
                <button
                  type="button"
                  onClick={() => add({ email: newColleague })}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{initials(newColleague)}</span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <b className="truncate">Add {newColleague}</b>
                    <span className="text-xs text-muted-foreground">Hasn’t signed in yet. They get an email, and access is ready when they do.</span>
                  </span>
                </button>
              </div>
            )}
            {outsideEmail && (
              <div className="absolute left-6 right-6 top-[52px] z-10 rounded-xl border border-border bg-popover py-1 shadow-xl">
                <button
                  type="button"
                  onClick={() => { setGuestEmails(outsideEmail); setQuery(''); scrollToGuests(); }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-external-soft text-external"><Link2 className="h-4 w-4" aria-hidden="true" /></span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <b className="truncate">Invite {outsideEmail} as a guest</b>
                    <span className="text-xs text-muted-foreground">Outside LampTwist: they get a secure link and a one-time code by email</span>
                  </span>
                </button>
              </div>
            )}
            <p className="pt-1.5 text-xs text-muted-foreground">
              Search colleagues by name, or type a @lamptwist.be address. They get an email.
              {target?.type === 'node' && ' For people outside LampTwist, use a guest link below.'}
            </p>
          </div>
        )}

        {state?.restricted && (
          <div className="mx-6 mt-4 flex gap-2.5 rounded-xl bg-accent px-3.5 py-3 text-sm text-accent-foreground">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Restricted folder — only the people below can open it. Access from parent folders doesn’t apply.</span>
          </div>
        )}

        <div className="px-6 pb-6 pt-4">
          <div className="pb-2 text-[13px] font-semibold text-muted-foreground">People with access</div>
          {!state && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {state && (
            <ul className="flex flex-col">
              {state.owner && (
                <li className="flex items-center gap-3 py-2">
                  <Principal entry={{ user: state.owner }} />
                  <span className="text-sm text-muted-foreground">Owner</span>
                </li>
              )}
              {state.entries.map((entry) => {
                const editable = !entry.inherited && canManage;
                return (
                  <li key={entry.id} className="flex items-center gap-3 py-2">
                    <Principal entry={entry} />
                    {editable ? (
                      <>
                        <RoleSelect value={entry.role} max={myRole} onChange={(r) => change(entry, r)} label={`Role for ${entry.user?.name || entry.team?.name}`} />
                        <button
                          type="button"
                          onClick={() => revoke(entry)}
                          aria-label={`Remove ${entry.user?.name || entry.team?.name}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">{entry.role_label}</span>
                    )}
                  </li>
                );
              })}
              {!state.owner && state.entries.length === 0 && (
                <li className="py-4 text-sm text-muted-foreground">Nobody has been added yet.</li>
              )}
            </ul>
          )}
        </div>
        {target?.type === 'node' && state && <div ref={guestsRef} className="scroll-mt-4"><GuestLinks item={item} role={myRole} prefill={guestEmails} /></div>}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
